import { Shield } from "lucide-react";

import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { formatPercentID } from "@/lib/format";
import { MetricCard } from "./metric-card";

interface GovernancePostureCardProps {
  value: number | null;
  band: string | null;
  isLoading: boolean;
  isError: boolean;
}

// Backend posture bands map to translation keys so the caption
// re-renders on a locale switch.
const BAND_KEY: Record<string, TranslationKey> = {
  excellent: "dashboard.governance.bandExcellent",
  good: "dashboard.governance.bandGood",
  needs_attention: "dashboard.governance.bandNeedsAttention",
  critical: "dashboard.governance.bandCritical",
};

function bandColor(band: string | null, value: number | null): string {
  if (band === "excellent" || band === "good" || (value ?? 0) >= 80) {
    return "text-success-600";
  }
  if (band === "needs_attention" || ((value ?? 0) >= 60 && (value ?? 0) < 80)) {
    return "text-warning-600";
  }
  if (band === "critical" || (value != null && value < 60)) {
    return "text-danger-600";
  }
  return "text-ocean-800";
}

export function GovernancePostureCard({
  value,
  band,
  isLoading,
  isError,
}: GovernancePostureCardProps) {
  const { t } = useTranslation();

  function bandLabel(): string {
    if (band && BAND_KEY[band]) return t(BAND_KEY[band]);
    if (band) return band;
    if (value == null) return t("dashboard.governance.noData");
    if (value >= 80) return t("dashboard.governance.bandHealthy");
    if (value >= 60) return t("dashboard.governance.bandApproachingThreshold");
    return t("dashboard.governance.bandNeedsAttention");
  }

  return (
    <MetricCard
      label={t("dashboard.governance.label")}
      icon={Shield}
      isLoading={isLoading}
      isError={isError}
    >
      {value == null ? (
        <div className="text-sm text-neutral-500">
          {t("dashboard.governance.noData")}
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span className={`text-3xl font-medium ${bandColor(band, value)}`}>
              {formatPercentID(value)}
            </span>
            <span className="text-sm text-neutral-500">%</span>
          </div>
          <div className="mt-2 text-xs text-neutral-600">{bandLabel()}</div>
        </>
      )}
    </MetricCard>
  );
}
